import { ActionModal, DialogPosition, Dialog } from '@ecdlink/ui';
import { useStoreSetup } from '@hooks/useStoreSetup';
import { useHistory } from 'react-router-dom';
import ROUTES from '@/routes/routes';
import { syncThunkActions } from '@/store/sync';
import { useAppDispatch } from '@/store';
import { useSelector } from 'react-redux';
import { practitionerSelectors } from '@/store/practitioner';
import { settingActions } from '@/store/settings';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { childrenSelectors, childrenActions } from '@store/children';
import { caregiverSelectors } from '@/store/caregiver';
import { authSelectors } from '@/store/auth';
import { ChildService } from '@services/ChildService';
import { UserService } from '@services/UserService';
import { caregiverActions } from '@store/caregiver';
import { userSelectors } from '@store/user';
import { classroomsActions, classroomsSelectors } from '@store/classroom';
import { sendQueryErrors } from '@/store/queryErrors/queryErrors.actions';

export const Logout: React.FC = () => {
  const { isOnline } = useOnlineStatus();

  const { resetAuth, resetAppStore } = useStoreSetup();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const user = useSelector(userSelectors.getUser);
  const isCoach = user?.roles?.some((role) => role.name === 'Coach');
  const practitioner = useSelector(practitionerSelectors?.getPractitioner);
  const sync = async () => {
    if (isCoach) {
      await dispatch(sendQueryErrors(user?.id || ''));
      await dispatch(syncThunkActions.syncOfflineDataForCoach({}));
    } else if (practitioner?.isPrincipal === true) {
      await dispatch(sendQueryErrors(user?.id || ''));
      await dispatch(syncThunkActions.syncOfflineData({}));
    } else {
      await dispatch(sendQueryErrors(user?.id || ''));
      await dispatch(syncThunkActions.syncOfflineDataForPractitioner({}));
    }

    dispatch(settingActions.setLastDataSync());
  };

  const children = useSelector(childrenSelectors.getChildren);
  const caregivers = useSelector(caregiverSelectors.getCaregivers);
  const childUsers = useSelector(childrenSelectors.getChildUsers);
  const auth = useSelector(authSelectors.getAuthUser);
  const learners = useSelector(classroomsSelectors.getClassroomGroupLearners);

  const deleteChildDuplicates = async () => {
    try {
      if (auth?.auth_token && childUsers && !isCoach) {
        const userService = new UserService(auth.auth_token);
        const childService = new ChildService(auth.auth_token);
        const unsyncedChildUsers = (childUsers ?? []).filter(
          (c) => (c as any)?.synced === false
        );

        for (const x of unsyncedChildUsers) {
          try {
            const existingUser = await userService.doesUserExist(x.id ?? '');

            if (existingUser) {
              continue;
            }

            var child = children?.find((c) => c.userId === x.id);

            var learner = learners?.find((l) => l.userId === x.id);

            var caregiver = caregivers?.find(
              (c) => c.id === child?.caregiverId
            );

            if (!child || !caregiver || !learner) {
              continue;
            }

            const duplicateChild =
              await childService.checkDuplicateChildForOfflineSync(
                x.firstName,
                x.surname,
                x.dateOfBirth?.toString(),
                x.idNumber,
                caregiver?.idNumber || caregiver?.passportNumber,
                x.genderId,
                learner?.classroomGroupId
              );

            if (duplicateChild) {
              const updatedChild = { ...child };
              const updatedCaregiver = { ...caregiver };
              const updatedLearner = { ...learner };
              updatedChild.isActive = false;
              updatedCaregiver.isActive = false;
              updatedLearner.isActive = false;
              dispatch(childrenActions.deactivateChild(updatedChild));
              dispatch(caregiverActions.updateCaregiver(updatedCaregiver));
              dispatch(
                classroomsActions.deactivateClassroomGroupLearner(
                  updatedLearner
                )
              );
            }
          } catch (err) {
            console.log(`Error processing child user ${x.id}:` + err);
            return false;
          }
        }
      }
    } catch (error) {
      console.error('Error in checkForChildDuplicates:', error);
      return false;
    }
  };

  const handleSync = async () => {
    if (isOnline) {
      await deleteChildDuplicates();
      await sync();
      await resetAppStore();
      await resetAuth();
      history.push('/');
    } else {
      history.push(ROUTES.LOGIN);
    }
  };

  return (
    <Dialog
      visible
      position={DialogPosition.Middle}
      fullScreen
      className="overflow-auto"
    >
      <ActionModal
        className={'mx-4'}
        title={'Are you sure you want to log out?'}
        importantText={''}
        icon={'ExclamationCircleIcon'}
        iconColor={'alertDark'}
        iconBorderColor={'alertBg'}
        actionButtons={[
          {
            text: 'Yes, log out',
            colour: 'primary',
            onClick: handleSync,
            type: 'filled',
            textColour: 'white',
            leadingIcon: 'CheckCircleIcon',
          },
          {
            text: 'No, cancel',
            textColour: 'white',
            colour: 'primary',
            type: 'filled',
            onClick: () => history.push(ROUTES.DASHBOARD),
            leadingIcon: 'XCircleIcon',
          },
        ]}
      />
    </Dialog>
  );
};
